import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { confirmPasswordReset } from 'firebase/auth';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { auth } from '../../config/firebase';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Field,
  FieldLabel,
  FieldError,
} from '../ui';

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export type ResetPasswordFormProps = {
  oobCode: string;
  loginPath: string;
  title?: string;
};

export function ResetPasswordForm({
  oobCode,
  loginPath,
  title = 'Choose a new password',
}: ResetPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const handleFormSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await confirmPasswordReset(auth, oobCode, data.password);
      setIsComplete(true);
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      if (code === 'auth/expired-action-code') {
        setErrorMessage('This reset link has expired. Please request a new one.');
      } else if (code === 'auth/invalid-action-code') {
        setErrorMessage('This reset link is invalid or has already been used.');
      } else if (code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please choose a stronger password.');
      } else {
        setErrorMessage('Could not reset your password. Please try again or request a new link.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isComplete) {
    return (
      <Card className="shadow-2xl border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <CardContent className="pt-8 pb-6 space-y-4 text-center">
          <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400 mx-auto" aria-hidden />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <Link
            to={loginPath}
            className="inline-block text-sm font-medium text-primary-600 hover:text-primary-800 dark:text-primary-300"
          >
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-2xl border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
      <CardHeader className="text-center pb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Enter a new password for your account.
        </p>
      </CardHeader>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <CardContent className="space-y-4">
          {errorMessage ? (
            <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 dark:bg-red-900/20">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" aria-hidden />
              <p className="text-sm text-red-800 dark:text-red-400">{errorMessage}</p>
            </div>
          ) : null}

          <Field data-invalid={!!errors.password?.message}>
            <FieldLabel htmlFor="new-password">New password</FieldLabel>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.password?.message}
              className="dark:border-gray-500 dark:bg-gray-900/60"
              {...register('password')}
            />
            <FieldError>{errors.password?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.confirmPassword?.message}>
            <FieldLabel htmlFor="confirm-password">Confirm password</FieldLabel>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.confirmPassword?.message}
              className="dark:border-gray-500 dark:bg-gray-900/60"
              {...register('confirmPassword')}
            />
            <FieldError>{errors.confirmPassword?.message}</FieldError>
          </Field>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full btn-primary" isLoading={isLoading} disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update password'}
          </Button>
          <Link
            to={loginPath}
            className="text-center text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
          >
            Back to sign in
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}
